/**
 * Sentry Error Tracking — Admin Panel
 *
 * DSN is read from VITE_SENTRY_DSN env var.
 * No DSN = Sentry disabled (zero network calls, zero overhead).
 *
 * Errors viewable at https://dopeassdevs.sentry.io (EU region)
 */

import * as Sentry from '@sentry/react';

let isInitialized = false;

const SENSITIVE_KEYS = new Set([
  'password', 'cvv', 'cardNumber', 'card_number',
  'encryptionKey', 'encryption_key', 'privateKey', 'private_key',
  'secret', 'api_key', 'apiKey', 'authorization', 'cookie',
  'refreshToken', 'idToken', 'accessToken',
]);

function scrubObject(obj: Record<string, any>): Record<string, any> {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(scrubObject);

  const scrubbed: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.has(key)) {
      scrubbed[key] = '[Filtered]';
    } else if (typeof value === 'object' && value !== null) {
      scrubbed[key] = scrubObject(value);
    } else {
      scrubbed[key] = value;
    }
  }
  return scrubbed;
}

/**
 * Initialize Sentry error tracking.
 * Call once at app startup (index.tsx, before ReactDOM.render).
 */
export function initSentry(): void {
  if (isInitialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.log('[Sentry] No DSN configured, error tracking disabled');
    return;
  }

  const host = window.location.hostname;
  const isDev = host.includes('localhost') || host.includes('dopeassdevs') || host.match(/^(\d+\.){3}\d+$/);
  const environment = import.meta.env.VITE_MODE
    || (isDev ? 'development' : 'staging');

  // Skip Sentry in local development — no error tracking, no console capturing, no network calls
  if (isDev) {
    console.log('[Sentry] Local development — error tracking disabled');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release: `deeliver-admin@${import.meta.env.VITE_VERSION || '0.0.0'}`,

    integrations: [
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
    ],

    // Send console.log/warn/error to Sentry Logs
    enableLogs: true,

    initialScope: {
      tags: {
        service_name: 'deeliver-admin',
      },
    },

    // No performance tracing (keep lightweight)
    tracesSampleRate: 0,

    // No session replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    // Scrub PII before sending
    beforeSend(event) {
      if (event.request?.headers) {
        event.request.headers = scrubObject(event.request.headers);
      }
      if (event.request?.data && typeof event.request.data === 'object') {
        event.request.data = scrubObject(event.request.data as Record<string, any>);
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => {
          if (bc.data) {
            bc.data = scrubObject(bc.data);
          }
          return bc;
        });
      }
      if (event.extra) {
        event.extra = scrubObject(event.extra as Record<string, any>);
      }
      return event;
    },

    ignoreErrors: [
      'Network request failed',
      'ECONNABORTED',
      'timeout of',
      'ResizeObserver loop',
      'Token refresh failed',
    ],
  });

  isInitialized = true;
  console.log(`[Sentry] Error tracking initialized (${environment}, admin-panel)`);
}

/**
 * Identify user in Sentry (call after login).
 */
export function identifySentryUser(userId: string, traits?: { email?: string; name?: string }): void {
  if (!isInitialized) return;

  Sentry.setUser({
    id: userId,
    ...(traits?.email && { email: traits.email }),
    ...(traits?.name && { username: traits.name }),
  });
}

/**
 * Clear user context (call on logout).
 */
export function clearSentryUser(): void {
  if (!isInitialized) return;
  Sentry.setUser(null);
}

/**
 * Capture an exception manually with optional extras.
 */
export function captureException(error: Error, extras?: Record<string, any>): void {
  if (!isInitialized) return;

  Sentry.withScope((scope) => {
    if (extras) {
      Object.entries(extras).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

export { Sentry };
